/**
 * REGISTERING THE WORKER — the part that makes the shell survive a lost signal.
 *
 * The ephemeris already travels with the app, so celestial works with no network the moment the page
 * is loaded. This is the other half: making sure the page can LOAD with no network.
 *
 * Registered only for the built app served under /study/. In dev the module graph changes on every
 * keystroke and a worker caching it would serve yesterday's code back to you — the classic way to
 * lose an afternoon to a bug you already fixed.
 *
 * A failure here is not fatal and must not be silent. The app works online without a worker; what a
 * candidate loses is the ability to open it at sea, and he should be able to find out that he lost
 * it BEFORE he needs it. The state is published on `navigator.__academyOffline` for the readiness
 * surface to read, rather than swallowed.
 */
const state = { supported: false, registered: false, controlling: false, error: null };

export function offlineState() { return { ...state }; }

export function registerOfflineWorker() {
  state.supported = 'serviceWorker' in navigator;
  if (!state.supported) {
    state.error = 'this browser has no service worker — the app needs a signal to open';
    return state;
  }
  // Dev serves modules unbundled and there is no generated sw.js to register.
  if (!location.pathname.startsWith('/study/')) {
    state.error = 'not the built app — no worker in dev';
    return state;
  }

  navigator.serviceWorker.register('/study/sw.js', { scope: '/study/' })
    .then((reg) => {
      state.registered = true;
      state.controlling = Boolean(navigator.serviceWorker.controller);
      /* ── ASK FOR A NEW WORKER ON EVERY LOAD ────────────────────────────────────────────────
         Registering an ALREADY-REGISTERED worker does not check for a new one on any schedule the
         page controls — the browser decides, and it can be hours. On 2026-08-19 the founder had a
         browser serving an old build while incognito, which has no worker at all, showed the
         current one. A hard reload does not help: it bypasses the HTTP cache and does not
         unregister a worker.
         `update()` re-fetches sw.js (served no-store, and never precached by itself) and, if the
         bytes differ, the new worker installs, calls skipWaiting and claims the page. One request
         per load, against a file that must be fetched anyway for correctness.
         A client already stranded by an OLD worker cannot be reached by this code — it is running
         the old bundle. That is what /academy-reset is for, and it lives outside this scope. */
      reg.update().catch(() => { /* offline, or the worker is fine — neither is an error worth raising */ });
      return reg;
    })
    .catch((err) => { state.error = String(err?.message || err); });

  navigator.serviceWorker.addEventListener('controllerchange', () => { state.controlling = true; });
  return state;
}

/* The readiness surface and the offline gate both need to see this without importing React. */
globalThis.__academyOffline = offlineState;
